import apiFetch from "@/src/utils/apiFetch";

import { Appointment, Patient, Service, SimpleExam } from "@/src/data/allTypes";

import { Button } from "@/src/components/buttons";
import {
  closeDialog,
  openConfirmDialog,
  openDialog,
  openDrawer,
} from "@/src/components/dialogs";
import { USER } from "@/src/data/globalVariables";
import { getItemId, isCabinet, isNurse } from "@/src/utils/utils";
import { openExamDemandDrawer } from "./examDemandButton";
import {
  openExamEditDrawer,
  openExamsVisualisationDrawer,
} from "./openExamVisualisationDrawer";
import { DocumentsCompartment } from "../documents/documentsCompartment";

export async function openExamActionsDialog({
  exams,
  allAppointments,
  index,
  patient,
  services,
  onChange,
}: {
  exams: SimpleExam[];
  allAppointments: Appointment[];
  index: number;
  patient: Patient;
  services: Service[];
  onChange: (exam: SimpleExam) => void;
}) {
  const exam = exams[index];
  const appointment = allAppointments.find(
    (appointment) => appointment.item == exam?.id
  );

  const canSendDemand =
    services?.length != 0 &&
    exam.status == "pending" &&
    !appointment &&
    !isCabinet();

  const documentTemplates = await apiFetch("documentTemplates", "loadMany", {
    types: ["standard", "letter"],
    referenceId: getItemId(exam.reference),
  });

  function close() {
    closeDialog("examStatusDialog");
  }

  return await openDialog({
    id: "examStatusDialog",
    title: "Statut de l'examen",
    content: (
      <div className="c1 gap10">
        {exam.status == "finished" && (
          <Button
            onClick={async () => {
              close();

              await openExamsVisualisationDrawer({
                exams: exams!,
                index,
                onEdit: async () => {
                  onChange(exam);
                  close();
                },
              });
            }}
          >
            Ouvrir
          </Button>
        )}
        {exam.status != "finished" && !isNurse(USER.get()) && (
          <Button
            onClick={async () => {
              close();

              await openExamEditDrawer({
                examId: exam.id,
                onConfirm: async (newExam) => {
                  onChange(newExam);
                  close();
                },
              });
            }}
          >
            Ajouter un résultat
          </Button>
        )}

        {canSendDemand && (
          <Button
            type={"tertiary"}
            onClick={async () => {
              await openExamDemandDrawer({
                exam,
                isHospitalized: true,
                patient,
                services,
                onConfirm: async (appointment) => {
                  onChange(exam);
                  close();
                },
              });
            }}
          >
            Envoyer une demande
          </Button>
        )}

        {documentTemplates?.length > 0 && (
          <Button
            type={"tertiary"}
            onClick={async () => {
              let fullExam = await apiFetch(
                "exams",
                "loadById",
                {
                  id: getItemId(exam),
                },
                "Erreur lors du chargement de l'examen"
              );
              if (!fullExam) throw new Error("Examen non trouvé");
              await openDrawer({
                id: "examDocumentTemplateChoiceDrawer",
                title: "Choisir un document",
                content: (
                  <div className="c1 gap10">
                    <DocumentsCompartment
                      patient={patient}
                      parent={fullExam}
                      referenceId={getItemId(fullExam?.reference)}
                      fieldAnswers={fullExam.content?.fieldAnswers || []}
                    />
                  </div>
                ),
              });
            }}
          >
            Imprimer documents
          </Button>
        )}

        <Button
          color="red"
          type={"tertiary"}
          onClick={async () => {
            openConfirmDialog({
              title: "Voulez-vous vraiment supprimer cet examen ?",
              content: "",
              onConfirm: async () => {
                await apiFetch("general", "deleteAny", {
                  id: exam.id,
                  table: "exams",
                });
                onChange(exam);
                close();
              },
            });
          }}
        >
          Supprimer
        </Button>
      </div>
    ),
  });
}
